import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, MapPin, RefreshCw, Trash2, Shield, KeyRound, Loader2, ShieldCheck, Megaphone, Trophy, Edit3, Ban, X, Flag, Mail, Send, Eye, MessageSquare, Bell } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { toast } from "sonner";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { subscribeReadySW } from "@/services/push";

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { refreshSettings } = useSettings();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [growth, setGrowth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [isSyncingPaid, setIsSyncingPaid] = useState(false);
  const [isImportingForeign, setIsImportingForeign] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);
  const [brokenCities, setBrokenCities] = useState([]);
  const [cityRepairInputs, setCityRepairInputs] = useState({});
  const [isSendingRecap, setIsSendingRecap] = useState(false);
  const [recapTargetEmail, setRecapTargetEmail] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [flashText, setFlashText] = useState("");
  const [flashSeverity, setFlashSeverity] = useState("info");
  const [reports, setReports] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  // Self-promotion form (only useful when no admin exists yet)
  const [bootstrapEmail, setBootstrapEmail] = useState("");
  const [bootstrapKey, setBootstrapKey] = useState("");
  const [showBootstrap, setShowBootstrap] = useState(false);
  const [terrainQuery, setTerrainQuery] = useState("");
  const [terrainResults, setTerrainResults] = useState([]);
  const [editingTerrain, setEditingTerrain] = useState(null);
  const [terrainSaving, setTerrainSaving] = useState(false);
  const [appSettings, setAppSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [pushOverview, setPushOverview] = useState(null);
  const [purgingPush, setPurgingPush] = useState(false);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [s, u, m, g, r, a, f, cfg, po] = await Promise.all([
        api.adminGetStats(),
        api.adminListUsers(),
        api.adminListMatches(),
        api.adminGetGrowth(),
        api.adminListReports(),
        api.adminGetAnalytics().catch(() => null),
        api.adminListFeedback().catch(() => []),
        api.adminGetSettings().catch(() => null),
        api.adminGetPushOverview().catch(() => null),
      ]);
      setStats(s); setUsers(u); setMatches(m); setGrowth(g); setReports(r);
      setAnalytics(a); setFeedback(f);
      if (cfg) setAppSettings(cfg);
      if (po) setPushOverview(po);
      setAccessDenied(false);
    } catch (err) {
      if ((err.message || "").toLowerCase().includes("administrateur")) {
        setAccessDenied(true);
      } else {
        toast.error(err.message || "Erreur de chargement");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/");
      return;
    }
    loadAll();
  }, [isAuthenticated, loadAll, navigate]);

  // --- Live presence: read from the global app-wide socket (App.js) ---
  useEffect(() => {
    if (typeof window.__matchupOnline === "number") {
      setOnlineCount(window.__matchupOnline);
      setWsConnected(true);
    }
    const onPresence = (e) => {
      setOnlineCount(e.detail);
      setWsConnected(true);
    };
    window.addEventListener("matchup:presence", onPresence);
    return () => window.removeEventListener("matchup:presence", onPresence);
  }, []);

  const handleBroadcast = async () => {
    if (!flashText.trim()) return toast.error("Texte du flash requis");
    try {
      await api.adminSetFlash(flashText, flashSeverity, 30);
      toast.success("Flash diffuse a tous les utilisateurs");
      setFlashText("");
    } catch (err) {
      toast.error(err.message || "Echec broadcast");
    }
  };

  const handleClearFlash = async () => {
    try { await api.adminClearFlash(); toast.success("Flash supprime"); } catch (err) { toast.error(err.message); }
  };

  const handleSaveUserEdit = async (updates) => {
    if (!editingUser) return;
    try {
      await api.adminUpdateUser(editingUser.id, updates);
      toast.success("Utilisateur mis a jour");
      setEditingUser(null);
      loadAll();
    } catch (err) {
      toast.error(err.message || "Echec MAJ");
    }
  };

  const handleImport = async () => {
    if (!confirm("Re-importer 4876 terrains depuis le GeoJSON ? Cette operation efface et reinsere la collection.")) return;
    setIsImporting(true);
    try {
      const res = await api.adminImportTerrains();
      toast.success(res.message || "Import termine", { description: `${res.total_terrains} terrains` });
      loadAll();
    } catch (err) {
      toast.error(err.message || "Echec import");
    } finally {
      setIsImporting(false);
    }
  };

  const terrainInputStyle = {
    minHeight: "40px", borderRadius: "10px", background: "#000",
    border: "1px solid #333", color: "#fff", padding: "0 12px", fontSize: "13px",
  };

  const searchTerrains = async () => {
    try {
      const res = await api.adminSearchTerrains(terrainQuery, 30);
      setTerrainResults(res || []);
      if (!res || res.length === 0) toast.info("Aucun terrain trouve");
    } catch (err) {
      toast.error(err.message || "Erreur recherche");
    }
  };

  const saveTerrain = async () => {
    if (!editingTerrain) return;
    const { id, nom, adresse, lat, lng } = editingTerrain;
    if (!nom || !nom.trim()) { toast.error("Nom requis"); return; }
    const la = parseFloat(lat), lo = parseFloat(lng);
    if (Number.isNaN(la) || Number.isNaN(lo)) { toast.error("Coordonnees invalides"); return; }
    setTerrainSaving(true);
    try {
      await api.adminUpdateTerrain(id, { nom: nom.trim(), adresse: (adresse || "").trim(), lat: la, lng: lo });
      toast.success("Terrain mis a jour");
      setTerrainResults((prev) => prev.map((t) => (t.id === id ? { ...t, nom: nom.trim(), adresse, lat: la, lng: lo } : t)));
      setEditingTerrain(null);
    } catch (err) {
      toast.error(err.message || "Echec mise a jour");
    } finally {
      setTerrainSaving(false);
    }
  };

  const updateSettingField = (key, value) => setAppSettings((prev) => ({ ...prev, [key]: value }));

  const togglePush = async (key) => {
    const next = !appSettings[key];
    setAppSettings((prev) => ({ ...prev, [key]: next }));
    try {
      const res = await api.adminUpdateSettings({ [key]: next });
      if (res) setAppSettings(res);
      refreshSettings();
    } catch (err) {
      setAppSettings((prev) => ({ ...prev, [key]: !next }));
      toast.error(err.message || "Echec de la mise a jour");
    }
  };

  const handlePurgePush = async () => {
    if (!window.confirm("Supprimer TOUS les abonnements push ? Les utilisateurs devront reactiver les notifications.")) return;
    setPurgingPush(true);
    try {
      const res = await api.adminPurgePush();
      toast.success(`${res.deleted} abonnement(s) push supprime(s)`);
      const po = await api.adminGetPushOverview().catch(() => null);
      if (po) setPushOverview(po);
    } catch (err) {
      toast.error(err.message || "Echec de la purge");
    } finally {
      setPurgingPush(false);
    }
  };

  const handleTestPush = async () => {
    // iOS: requestPermission MUST be the first call of the gesture, before any await.
    let permission = "default";
    try {
      permission = await Notification.requestPermission();
    } catch (err) {
      console.error("requestPermission failed", err);
    }
    if (permission !== "granted") {
      toast.error("Autorise les notifications dans les reglages pour tester");
      return;
    }
    setTestingPush(true);
    try {
      await subscribeReadySW();
      const res = await api.pushTest();
      if (res.sent > 0) {
        toast.success("Notification de test envoyee ! Verifie ton ecran.");
      } else if (!res.subscriptions) {
        toast.error("Aucun appareil abonne. Reactive l'alerte puis reessaie.");
      } else {
        toast.error("Echec d'envoi (token rejete par Apple/Google).");
      }
    } catch (err) {
      toast.error(err.message || "Echec du test");
    } finally {
      setTestingPush(false);
    }
  };

  const saveSettings = async () => {
    if (!appSettings) return;
    setSavingSettings(true);
    try {
      const payload = {
        text_modal_push: appSettings.text_modal_push,
        text_no_match: appSettings.text_no_match,
        search_radius_km: parseInt(appSettings.search_radius_km, 10) || 20,
        email_welcome_subject: appSettings.email_welcome_subject,
        email_welcome_body: appSettings.email_welcome_body,
        email_reminder_body: appSettings.email_reminder_body,
        maintenance_mode: !!appSettings.maintenance_mode,
        max_days_advance: parseInt(appSettings.max_days_advance, 10) || 14,
        max_players_limit: parseInt(appSettings.max_players_limit, 10) || 22,
      };
      const res = await api.adminUpdateSettings(payload);
      setAppSettings(res);
      refreshSettings();
      toast.success("Configuration enregistree");
    } catch (err) {
      toast.error(err.message || "Echec enregistrement");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleImportForeign = async () => {
    if (!confirm("Ajouter les derniers terrains (Suisse/Genève + Belgique) sur cet environnement ? (ajout uniquement, aucun terrain existant supprimé)")) return;
    setIsImportingForeign(true);
    try {
      const res = await api.adminImportForeignTerrains();
      toast.success("Terrains ajoutés", { description: res.message });
      loadAll();
    } catch (err) {
      toast.error(err.message || "Echec ajout terrains");
    } finally {
      setIsImportingForeign(false);
    }
  };

  const handleSyncPaidCenters = async () => {
    setIsSyncingPaid(true);
    try {
      const res = await api.adminSeedPaidCenters();
      toast.success("Centres payants synchronises", {
        description: `+${res.inserted} ajoutes • ${res.updated} a jour • ${res.backfilled_existing} mis a payant`,
      });
      loadAll();
    } catch (err) {
      toast.error(err.message || "Echec synchronisation");
    } finally {
      setIsSyncingPaid(false);
    }
  };

  const loadBrokenCities = async () => {
    try {
      const res = await api.adminListMissingCities();
      setBrokenCities(res.users || []);
    } catch (err) {
      toast.error(err.message || "Impossible de charger les utilisateurs");
    }
  };

  const handleRepairAll = async () => {
    if (!confirm("Tenter une reparation automatique des coordonnees pour tous les utilisateurs ?")) return;
    setIsRepairing(true);
    try {
      const res = await api.adminRepairAllCities();
      toast.success("Reparation terminee", {
        description: `Mis a jour: ${res.updated} • Deja ok: ${res.skipped_already_ok} • Echecs: ${res.failed}`,
      });
      await loadBrokenCities();
    } catch (err) {
      toast.error(err.message || "Echec reparation");
    } finally {
      setIsRepairing(false);
    }
  };

  const handleRepairUser = async (userId) => {
    const city = (cityRepairInputs[userId] || "").trim();
    if (!city) {
      toast.error("Saisis une ville d'abord");
      return;
    }
    try {
      const res = await api.adminRepairUserCity(userId, city);
      toast.success(`${city} -> ${res.city_lat.toFixed(2)}, ${res.city_lng.toFixed(2)}`);
      setBrokenCities((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      toast.error(err.message || `Ville '${city}' introuvable`);
    }
  };

  const handleBootstrap = async (e) => {
    e.preventDefault();
    if (!bootstrapEmail || !bootstrapKey) return;
    try {
      await api.adminBootstrap(bootstrapEmail, bootstrapKey);
      toast.success(`${bootstrapEmail} est maintenant admin. Reconnecte-toi.`);
      setBootstrapEmail("");
      setBootstrapKey("");
      setTimeout(() => loadAll(), 600);
    } catch (err) {
      toast.error(err.message || "Bootstrap refuse");
    }
  };

  const handleDelete = async (uid, uname) => {
    if (!confirm(`Supprimer definitivement le compte ${uname} ?`)) return;
    try {
      await api.adminDeleteUser(uid);
      toast.success("Utilisateur supprime");
      loadAll();
    } catch (err) {
      toast.error(err.message || "Suppression echouee");
    }
  };

  const handleSendRecap = async ({ dryRun, targeted }) => {
    const target = targeted ? (recapTargetEmail || "").trim() : null;
    if (targeted && !target) {
      toast.error("Renseigne un email cible d'abord");
      return;
    }
    if (!dryRun && !targeted) {
      if (!confirm("Envoyer le recap a TOUS les utilisateurs actifs (derniers 30j, opt-in) ?")) return;
    }
    setIsSendingRecap(true);
    try {
      const res = await api.adminSendWeeklyRecap({ dryRun, targetEmail: target });
      const desc = `Cibles: ${res.targeted} | Envoyes: ${res.sent} | Echecs: ${res.failed}`;
      if (dryRun) {
        toast.success("Apercu (dry run) genere", { description: desc });
      } else if (res.failed > 0 && res.sent === 0) {
        toast.error("Aucun email envoye", { description: desc });
      } else {
        toast.success("Recap envoye", { description: desc });
      }
    } catch (err) {
      toast.error(err.message || "Erreur envoi recap");
    } finally {
      setIsSendingRecap(false);
    }
  };

  // Access denied screen (also offers bootstrap form)
  if (accessDenied || (user && user.role !== "admin")) {
    return (
      <div data-testid="admin-access-denied" style={{ minHeight: "100vh", backgroundColor: "#000", color: "#fff", padding: "24px 16px" }}>
        <header style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "32px" }}>
          <button onClick={() => navigate("/")} style={{ padding: "8px", background: "none", border: "none", color: "#fff", cursor: "pointer" }}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading" style={{ fontSize: "18px", fontWeight: 700 }}>Acces administrateur</h1>
        </header>

        <div style={{ maxWidth: "440px", margin: "0 auto", padding: "20px", backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
            <ShieldCheck size={22} color="#eab308" />
            <h2 style={{ fontSize: "16px", fontWeight: 700 }}>Tu n'es pas admin</h2>
          </div>
          <p style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "20px", lineHeight: 1.5 }}>
            Si tu es le proprietaire de l'application, utilise la cle de bootstrap (configuree via la variable
            d'env <code style={{ color: "#22c55e" }}>ADMIN_BOOTSTRAP_KEY</code>) pour t'auto-promouvoir, puis reconnecte-toi.
          </p>

          <button
            data-testid="show-bootstrap-btn"
            onClick={() => setShowBootstrap(s => !s)}
            style={{ marginBottom: "16px", fontSize: "12px", color: "#22c55e", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
          >
            <KeyRound size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: "4px" }} />
            {showBootstrap ? "Masquer" : "Afficher la procedure de bootstrap"}
          </button>

          {showBootstrap && (
            <form onSubmit={handleBootstrap} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <input
                type="email" required
                data-testid="bootstrap-email-input"
                placeholder="Ton email (deja inscrit)"
                value={bootstrapEmail}
                onChange={(e) => setBootstrapEmail(e.target.value)}
                style={{ minHeight: "44px", padding: "0 14px", borderRadius: "10px", backgroundColor: "#000", border: "1px solid #333", color: "#fff", fontSize: "14px" }}
              />
              <input
                type="password" required
                data-testid="bootstrap-key-input"
                placeholder="Cle de bootstrap"
                value={bootstrapKey}
                onChange={(e) => setBootstrapKey(e.target.value)}
                style={{ minHeight: "44px", padding: "0 14px", borderRadius: "10px", backgroundColor: "#000", border: "1px solid #333", color: "#fff", fontSize: "14px" }}
              />
              <button
                type="submit"
                data-testid="bootstrap-submit-btn"
                style={{ minHeight: "46px", borderRadius: "10px", background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", border: "none", color: "#fff", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}
              >
                M'auto-promouvoir admin
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (isLoading || !stats) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#000", color: "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
        <Loader2 size={20} className="animate-spin" />
        <span>Chargement du dashboard...</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" style={{ minHeight: "100vh", backgroundColor: "#000", color: "#fff", padding: "16px", paddingBottom: "100px" }}>
      <header style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
        <button data-testid="admin-back-btn" onClick={() => navigate("/")} style={{ padding: "8px", background: "none", border: "none", color: "#fff", cursor: "pointer" }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 className="font-heading" style={{ fontSize: "20px", fontWeight: 800 }}>Admin Dashboard</h1>
          <p style={{ fontSize: "11px", color: "#22c55e" }}>
            <Shield size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: "4px" }} />
            {user?.email} · admin
          </p>
        </div>
        <button
          data-testid="admin-refresh-btn"
          onClick={loadAll}
          style={{ padding: "10px", borderRadius: "10px", backgroundColor: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", color: "#22c55e", cursor: "pointer" }}
        >
          <RefreshCw size={16} />
        </button>
      </header>

      {/* Stats KPI */}
      <section data-testid="admin-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "20px" }}>
        <KPI label="Utilisateurs" value={stats.total_users} icon={<Users size={16} />} />
        <KPI label="Terrains en base" value={stats.total_terrains} icon={<MapPin size={16} />} accent />
        <KPI label="Matchs total" value={stats.total_matches} icon={<Users size={16} />} />
        <KPI label="Matchs termines" value={stats.completed_matches} icon={<Users size={16} />} />
      </section>

      {/* Live presence counter */}
      <section data-testid="live-presence-card" style={{ marginBottom: "20px" }}>
        <div
          style={{
            backgroundColor: "#0a0a0a",
            border: "1px solid #1a1a1a",
            borderRadius: "14px",
            padding: "16px",
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "44px",
              height: "44px",
              borderRadius: "12px",
              backgroundColor: "rgba(34,197,94,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Users size={20} style={{ color: "#22c55e" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                data-testid="online-count"
                className="font-sport"
                style={{ fontSize: "28px", lineHeight: 1, color: "#fff" }}
              >
                {onlineCount}
              </span>
              <span style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 600 }}>
                en ligne
              </span>
            </div>
            <p style={{ fontSize: "11px", color: "#6b7280", marginTop: "4px" }}>
              Utilisateurs connectes en temps reel
            </p>
          </div>
          <span
            data-testid="ws-status-dot"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "11px",
              fontWeight: 700,
              color: wsConnected ? "#22c55e" : "#6b7280",
            }}
          >
            <span
              style={{
                width: "9px",
                height: "9px",
                borderRadius: "50%",
                backgroundColor: wsConnected ? "#22c55e" : "#4b5563",
                boxShadow: wsConnected ? "0 0 0 4px rgba(34,197,94,0.18)" : "none",
              }}
            />
            {wsConnected ? "LIVE" : "Hors ligne"}
          </span>
        </div>
        <button
          data-testid="test-push-btn"
          onClick={handleTestPush}
          disabled={testingPush}
          style={{
            marginTop: 10, width: "100%", minHeight: 44, borderRadius: 12,
            background: "transparent", border: "1px solid #22c55e", color: "#22c55e",
            fontSize: 13, fontWeight: 700, cursor: testingPush ? "wait" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            opacity: testingPush ? 0.6 : 1,
          }}
        >
          <Bell size={15} /> {testingPush ? "Envoi..." : "Tester les notifications"}
        </button>
      </section>

      {/* Push subscriptions overview */}
      {pushOverview && (
        <section data-testid="push-overview-section" style={{ marginBottom: "24px" }}>
          <div style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "14px", padding: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Bell size={16} style={{ color: "#22c55e" }} />
                <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700 }}>Abonnements push</h2>
              </div>
              <span data-testid="push-active-count" className="font-sport" style={{ fontSize: 22, color: "#22c55e", lineHeight: 1 }}>
                {pushOverview.total_subscriptions}
              </span>
            </div>
            <p style={{ fontSize: "11px", color: "#6b7280", marginBottom: "12px", lineHeight: 1.4 }}>
              Appareils actuellement abonnes aux notifications. Purge la base apres une rotation de cle VAPID.
            </p>
            <button
              data-testid="purge-push-btn"
              onClick={handlePurgePush}
              disabled={purgingPush}
              style={{
                width: "100%", minHeight: 44, borderRadius: 12, marginBottom: 14,
                background: "transparent", border: "1px solid #ef4444", color: "#ef4444",
                fontSize: 13, fontWeight: 700, cursor: purgingPush ? "wait" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                opacity: purgingPush ? 0.6 : 1,
              }}
            >
              <Trash2 size={15} /> {purgingPush ? "Purge..." : "Purger les abonnements push"}
            </button>

            <div data-testid="push-users-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {pushOverview.users.map((u) => (
                <div
                  key={u.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", borderRadius: 10,
                    background: "#000", border: "1px solid #1f1f1f",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {u.name || u.email}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {u.email}
                    </div>
                    <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                      {(u.city || "Ville inconnue")} · {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                    </div>
                  </div>
                  <span
                    style={{
                      flexShrink: 0, fontSize: 10, fontWeight: 800, padding: "4px 9px", borderRadius: 999,
                      letterSpacing: "0.04em",
                      background: u.push_active ? "rgba(34,197,94,0.12)" : "rgba(107,114,128,0.12)",
                      color: u.push_active ? "#22c55e" : "#9ca3af",
                      border: u.push_active ? "1px solid rgba(34,197,94,0.4)" : "1px solid #2a2a2a",
                    }}
                  >
                    {u.push_active ? "PUSH ON" : "OFF"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Flash Message Broadcast */}
      <section style={{ marginBottom: "24px" }}>
        <div style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "14px", padding: "16px" }}>
          <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700, marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Megaphone size={16} color="#3b82f6" /> Flash Message
          </h2>
          <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "12px" }}>
            Diffuse un bandeau d'info en haut de l'app pour tous les utilisateurs (30 min).
          </p>
          <textarea
            data-testid="flash-input"
            value={flashText}
            onChange={(e) => setFlashText(e.target.value)}
            placeholder="Maintenance prevue ce soir 22h..."
            rows={2}
            style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", backgroundColor: "#000", border: "1px solid #333", color: "#fff", fontSize: "14px", resize: "none", fontFamily: "inherit" }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "8px", marginTop: "10px" }}>
            <select data-testid="flash-severity" value={flashSeverity} onChange={(e) => setFlashSeverity(e.target.value)}
              style={{ padding: "0 12px", borderRadius: "10px", backgroundColor: "#000", border: "1px solid #333", color: "#fff", minHeight: "44px" }}>
              <option value="info">Info</option>
              <option value="warning">Avertissement</option>
              <option value="success">Succes</option>
            </select>
            <button data-testid="flash-broadcast-btn" onClick={handleBroadcast}
              style={{ padding: "0 16px", borderRadius: "10px", background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", border: "none", color: "#fff", fontWeight: 700, fontSize: "13px", cursor: "pointer", minHeight: "44px" }}>
              Diffuser
            </button>
            <button data-testid="flash-clear-btn" onClick={handleClearFlash}
              style={{ padding: "0 12px", borderRadius: "10px", backgroundColor: "transparent", border: "1px solid #333", color: "#9ca3af", cursor: "pointer", minHeight: "44px" }}>
              <X size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* Growth chart */}
      {growth && growth.series && growth.series.length > 0 && (
        <section style={{ marginBottom: "24px" }}>
          <div style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "14px", padding: "16px" }}>
            <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700, marginBottom: "10px" }}>Croissance (30j)</h2>
            <div data-testid="growth-chart" style={{ height: 180, marginLeft: "-8px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={growth.series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 9 }} />
                  <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: "#000", border: "1px solid #333", borderRadius: "8px", fontSize: "12px" }} />
                  <Line type="monotone" dataKey="users" stroke="#22c55e" strokeWidth={2} dot={false} name="Inscriptions" />
                  <Line type="monotone" dataKey="matches" stroke="#eab308" strokeWidth={2} dot={false} name="Matchs" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {growth.top_cities && growth.top_cities.length > 0 && (
              <>
                <h3 style={{ fontSize: "12px", color: "#9ca3af", marginTop: "16px", marginBottom: "8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Top villes</h3>
                <div style={{ height: 120 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={growth.top_cities} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                      <XAxis dataKey="city" stroke="#6b7280" tick={{ fontSize: 9 }} />
                      <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ backgroundColor: "#000", border: "1px solid #333", borderRadius: "8px", fontSize: "12px" }} />
                      <Bar dataKey="count" fill="#22c55e" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Reports / Moderation */}
      <section style={{ marginBottom: "24px" }}>
        <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700, marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
          <Flag size={16} color="#ef4444" /> Signalements ({reports.length})
        </h2>
        <div data-testid="admin-reports-list" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {reports.length === 0 ? (
            <div style={{ padding: "16px", textAlign: "center", color: "#6b7280", fontSize: "12px", backgroundColor: "#0a0a0a", borderRadius: "12px", border: "1px solid #1a1a1a" }}>
              Aucun signalement.
            </div>
          ) : (
            reports.slice(0, 30).map(r => {
              const motifLabel = { absence: "Absence", comportement: "Comportement", tricherie: "Tricherie" }[r.motif] || r.motif;
              const motifCol = { absence: "#eab308", comportement: "#f97316", tricherie: "#ef4444" }[r.motif] || "#6b7280";
              return (
                <div key={r.id} style={{ padding: "12px 14px", backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                    <span style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
                      {r.target?.name || "?"}
                    </span>
                    <span style={{ fontSize: "10px", padding: "3px 8px", borderRadius: "999px", backgroundColor: `${motifCol}22`, color: motifCol, fontWeight: 700, textTransform: "uppercase" }}>
                      {motifLabel}
                    </span>
                  </div>
                  <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                    Signale par <strong style={{ color: "#d1d5db" }}>{r.reporter?.name || "?"}</strong>
                    {" · "}
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : "—"}
                  </div>
                  {r.message && (
                    <p style={{ fontSize: "12px", color: "#d1d5db", marginTop: "6px", padding: "6px 10px", backgroundColor: "#000", borderRadius: "8px", borderLeft: `2px solid ${motifCol}` }}>
                      "{r.message}"
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Matches table */}
      <section style={{ marginBottom: "24px" }}>
        <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700, marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
          <MessageSquare size={16} color="#22c55e" /> Retours communaute ({feedback.length})
        </h2>
        <div data-testid="admin-feedback-table" style={{ overflowX: "auto", backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "12px" }}>
          {feedback.length === 0 ? (
            <div style={{ padding: "16px", textAlign: "center", color: "#6b7280", fontSize: "12px" }}>
              Aucun retour pour le moment.
            </div>
          ) : (
            <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#0f0f0f", color: "#9ca3af", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px" }}>Pseudo</th>
                  <th style={{ padding: "10px 12px" }}>Type</th>
                  <th style={{ padding: "10px 12px" }}>Message</th>
                  <th style={{ padding: "10px 12px" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {feedback.map(f => {
                  const isBug = f.type === "bug";
                  return (
                    <tr key={f.id} style={{ borderTop: "1px solid #1a1a1a" }}>
                      <td style={{ padding: "10px 12px", color: "#fff", fontWeight: 600 }}>{f.user?.name || "?"}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{
                          fontSize: 10, padding: "3px 8px", borderRadius: 999,
                          background: isBug ? "rgba(239,68,68,0.15)" : "rgba(250,204,21,0.15)",
                          color: isBug ? "#f87171" : "#fbbf24",
                          fontWeight: 700, textTransform: "uppercase",
                        }}>{isBug ? "Bug" : "Idee"}</span>
                      </td>
                      <td style={{ padding: "10px 12px", color: "#d1d5db", maxWidth: 360, whiteSpace: "pre-wrap" }}>{f.content}</td>
                      <td style={{ padding: "10px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>
                        {f.created_at ? new Date(f.created_at).toLocaleDateString("fr-FR") : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Matches table */}
      <section style={{ marginBottom: "24px" }}>
        <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700, marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
          <Trophy size={16} /> Matchs ({matches.length})
        </h2>
        <div data-testid="admin-matches-table" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {matches.length === 0 ? (
            <div style={{ padding: "24px", textAlign: "center", color: "#6b7280", fontSize: "13px", backgroundColor: "#0a0a0a", borderRadius: "12px", border: "1px solid #1a1a1a" }}>
              Aucun match en base.
            </div>
          ) : (
            matches.slice(0, 50).map(m => {
              const statusCol = m.status === "completed" ? "#22c55e" : m.status === "cancelled" ? "#ef4444" : "#3b82f6";
              return (
                <div key={m.id} style={{ padding: "12px 14px", backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: "#fff" }}>{m.title}</span>
                    <span style={{ fontSize: "10px", padding: "3px 8px", borderRadius: "999px", backgroundColor: `${statusCol}22`, color: statusCol, fontWeight: 700, textTransform: "uppercase" }}>
                      {m.status}
                    </span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                    {m.location} · {m.date} {m.time} · {m.players_count}/{m.max_players}
                  </div>
                  <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "3px" }}>
                    Cree par {m.organizer || "?"}
                  </div>
                </div>
              );
            })
          )}
          {matches.length > 50 && (
            <p style={{ fontSize: "11px", color: "#9ca3af", textAlign: "center" }}>+{matches.length - 50} autres</p>
          )}
        </div>
      </section>

      {/* Terrains panel */}
      <section style={{ marginBottom: "24px" }}>
        <div style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "14px", padding: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
            <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700 }}>Terrains GeoJSON</h2>
            <span style={{ fontSize: "11px", color: stats.total_terrains > 0 ? "#22c55e" : "#ef4444", fontWeight: 600 }}>
              {stats.total_terrains > 0 ? `${stats.total_terrains} indexes` : "Base vide"}
            </span>
          </div>
          <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "14px", lineHeight: 1.5 }}>
            Si la carte n'affiche aucun pin, c'est probablement parce que la collection est vide sur cet environnement. Lance l'import.
          </p>
          <button
            data-testid="reimport-terrains-btn"
            onClick={handleImport}
            disabled={isImporting}
            style={{
              minHeight: "48px", width: "100%", borderRadius: "12px",
              background: isImporting ? "#1a1a1a" : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              border: "none", color: "#fff", fontSize: "14px", fontWeight: 700,
              cursor: isImporting ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            {isImporting ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {isImporting ? "Import en cours (peut prendre 30s)..." : "Relancer l'import GeoJSON"}
          </button>

          <button
            data-testid="sync-paid-centers-btn"
            onClick={handleSyncPaidCenters}
            disabled={isSyncingPaid}
            style={{
              marginTop: "10px",
              minHeight: "44px", width: "100%", borderRadius: "12px",
              background: isSyncingPaid ? "#1a1a1a" : "transparent",
              border: "1px solid rgba(234,179,8,0.5)",
              color: "#eab308", fontSize: "13px", fontWeight: 700,
              cursor: isSyncingPaid ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            {isSyncingPaid ? <Loader2 size={14} className="animate-spin" /> : <span style={{ fontSize: "14px", fontWeight: 900 }}>€</span>}
            {isSyncingPaid ? "Synchronisation..." : "Synchroniser Centres Payants"}
          </button>

          <button
            data-testid="import-foreign-terrains-btn"
            onClick={handleImportForeign}
            disabled={isImportingForeign}
            style={{
              marginTop: "10px",
              minHeight: "44px", width: "100%", borderRadius: "12px",
              background: isImportingForeign ? "#1a1a1a" : "transparent",
              border: "1px solid rgba(59,130,246,0.5)",
              color: "#60a5fa", fontSize: "13px", fontWeight: 700,
              cursor: isImportingForeign ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            {isImportingForeign ? <Loader2 size={14} className="animate-spin" /> : <span style={{ fontSize: "14px" }}>🇨🇭🇧🇪</span>}
            {isImportingForeign ? "Ajout en cours..." : "Ajouter les nouveaux terrains"}
          </button>

          {/* Manual terrain editor (name / address / GPS) */}
          <div style={{ marginTop: "16px", borderTop: "1px solid #1a1a1a", paddingTop: "14px" }}>
            <h3 className="font-heading" style={{ fontSize: "13px", fontWeight: 700, marginBottom: "8px" }}>Editer un terrain</h3>
            <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
              <input
                data-testid="terrain-search-input"
                value={terrainQuery}
                onChange={(e) => setTerrainQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") searchTerrains(); }}
                placeholder="Rechercher (nom / ville)"
                style={{ ...terrainInputStyle, flex: 1, minHeight: "42px" }}
              />
              <button
                data-testid="terrain-search-btn"
                onClick={searchTerrains}
                style={{ minHeight: "42px", padding: "0 16px", borderRadius: "10px", background: "#22c55e", border: "none", color: "#fff", fontWeight: 700, cursor: "pointer" }}
              >
                Chercher
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {terrainResults.map((t) => (
                <div key={t.id} data-testid={`terrain-row-${t.id}`} style={{ background: "#000", border: "1px solid #1a1a1a", borderRadius: "10px", padding: "10px" }}>
                  {editingTerrain?.id === t.id ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <input data-testid="edit-terrain-nom" value={editingTerrain.nom} onChange={(e) => setEditingTerrain({ ...editingTerrain, nom: e.target.value })} placeholder="Nom" style={terrainInputStyle} />
                      <input data-testid="edit-terrain-adresse" value={editingTerrain.adresse} onChange={(e) => setEditingTerrain({ ...editingTerrain, adresse: e.target.value })} placeholder="Adresse" style={terrainInputStyle} />
                      <div style={{ display: "flex", gap: "8px" }}>
                        <input data-testid="edit-terrain-lat" value={editingTerrain.lat} onChange={(e) => setEditingTerrain({ ...editingTerrain, lat: e.target.value })} placeholder="Latitude" style={{ ...terrainInputStyle, flex: 1 }} />
                        <input data-testid="edit-terrain-lng" value={editingTerrain.lng} onChange={(e) => setEditingTerrain({ ...editingTerrain, lng: e.target.value })} placeholder="Longitude" style={{ ...terrainInputStyle, flex: 1 }} />
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button data-testid="save-terrain-btn" onClick={saveTerrain} disabled={terrainSaving} style={{ flex: 1, minHeight: "40px", borderRadius: "10px", background: "#22c55e", border: "none", color: "#fff", fontWeight: 700, cursor: terrainSaving ? "not-allowed" : "pointer" }}>{terrainSaving ? "..." : "Enregistrer"}</button>
                        <button onClick={() => setEditingTerrain(null)} style={{ flex: 1, minHeight: "40px", borderRadius: "10px", background: "transparent", border: "1px solid #333", color: "#9ca3af", fontWeight: 700, cursor: "pointer" }}>Annuler</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: "13px", fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.nom}</p>
                        <p style={{ fontSize: "11px", color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {t.ville || "?"} · {t.lat != null ? Number(t.lat).toFixed(5) : "-"}, {t.lng != null ? Number(t.lng).toFixed(5) : "-"}
                        </p>
                      </div>
                      <button
                        data-testid={`edit-terrain-${t.id}`}
                        onClick={() => setEditingTerrain({ id: t.id, nom: t.nom || "", adresse: t.adresse || "", lat: t.lat ?? "", lng: t.lng ?? "" })}
                        style={{ flexShrink: 0, minHeight: "36px", padding: "0 14px", borderRadius: "9px", background: "transparent", border: "1px solid #22c55e", color: "#22c55e", fontWeight: 700, cursor: "pointer" }}
                      >
                        Modifier
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* App configuration (dynamic settings) */}
      {appSettings && (
        <section style={{ marginBottom: "24px" }}>
          <div style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "14px", padding: "16px" }}>
            <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700, marginBottom: "4px" }}>Configuration de l'app</h2>
            <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "14px", lineHeight: 1.5 }}>
              Pilote dynamiquement les textes, le rayon de recherche, les emails et le bandeau d'alerte.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                { k: "text_modal_push", label: "Texte modale notifications", area: true },
                { k: "text_no_match", label: "Texte si aucun match", area: true },
                { k: "email_welcome_subject", label: "Email inscription — Sujet" },
                { k: "email_welcome_body", label: "Email inscription — Contenu ({name})", area: true },
                { k: "email_reminder_body", label: "Email rappel H-2 ({title} {time} {location})", area: true },
              ].map(({ k, label, area }) => (
                <div key={k} style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  <label style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 600 }}>{label}</label>
                  {area ? (
                    <textarea
                      data-testid={`setting-${k}`}
                      value={appSettings[k] || ""}
                      onChange={(e) => updateSettingField(k, e.target.value)}
                      rows={2}
                      style={{ ...terrainInputStyle, minHeight: "52px", padding: "8px 12px", resize: "vertical" }}
                    />
                  ) : (
                    <input
                      data-testid={`setting-${k}`}
                      value={appSettings[k] || ""}
                      onChange={(e) => updateSettingField(k, e.target.value)}
                      style={terrainInputStyle}
                    />
                  )}
                </div>
              ))}
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "5px" }}>
                  <label style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 600 }}>Rayon de recherche (km)</label>
                  <input data-testid="setting-search_radius_km" type="number" min="1" max="200" value={appSettings.search_radius_km ?? 20} onChange={(e) => updateSettingField("search_radius_km", e.target.value)} style={terrainInputStyle} />
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "5px" }}>
                  <label style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 600 }}>Jours max a l'avance</label>
                  <input data-testid="setting-max_days_advance" type="number" min="1" max="365" value={appSettings.max_days_advance ?? 14} onChange={(e) => updateSettingField("max_days_advance", e.target.value)} style={terrainInputStyle} />
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "5px" }}>
                  <label style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 600 }}>Max joueurs</label>
                  <input data-testid="setting-max_players_limit" type="number" min="2" max="50" value={appSettings.max_players_limit ?? 22} onChange={(e) => updateSettingField("max_players_limit", e.target.value)} style={terrainInputStyle} />
                </div>
              </div>
              <label
                data-testid="setting-maintenance_mode"
                className="flex items-center justify-between cursor-pointer select-none"
                style={{ padding: "12px 14px", borderRadius: 10, background: "#000", border: "1px solid #1f1f1f" }}
              >
                <span style={{ fontSize: 13, color: "#d1d5db", fontWeight: 600 }}>Mode maintenance (bloque les non-admins)</span>
                <input
                  type="checkbox"
                  checked={!!appSettings.maintenance_mode}
                  onChange={(e) => updateSettingField("maintenance_mode", e.target.checked)}
                  style={{ width: 20, height: 20, accentColor: "#22c55e" }}
                />
              </label>

              {/* Push notification toggles */}
              <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: "12px", marginTop: "2px" }}>
                <label style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  Notifications push
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }}>
                  {[
                    { k: "push_signup", label: "Nouvel inscrit (vers admin)" },
                    { k: "push_new_match", label: "Nouveau match a proximite" },
                    { k: "push_join", label: "Joueur rejoint (vers organisateur)" },
                    { k: "push_flash", label: "Message flash (broadcast)" },
                  ].map(({ k, label }) => {
                    const on = appSettings[k] !== false;
                    return (
                      <button
                        key={k}
                        type="button"
                        data-testid={`push-toggle-${k}`}
                        onClick={() => togglePush(k)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 14px",
                          borderRadius: 10,
                          background: "#000",
                          border: "1px solid #1f1f1f",
                          cursor: "pointer",
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        <span style={{ fontSize: 13, color: "#d1d5db", fontWeight: 600 }}>{label}</span>
                        <span
                          aria-checked={on}
                          role="switch"
                          style={{
                            position: "relative",
                            flexShrink: 0,
                            width: 42,
                            height: 24,
                            borderRadius: 999,
                            transition: "background-color 0.2s ease",
                            backgroundColor: on ? "#22c55e" : "#3f3f46",
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              top: 3,
                              left: on ? 21 : 3,
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                              transition: "left 0.2s ease",
                            }}
                          />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                data-testid="save-settings-btn"
                onClick={saveSettings}
                disabled={savingSettings}
                style={{
                  minHeight: "48px", width: "100%", borderRadius: "12px",
                  background: savingSettings ? "#1a1a1a" : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  border: "none", color: "#fff", fontSize: "14px", fontWeight: 700,
                  cursor: savingSettings ? "not-allowed" : "pointer",
                }}
              >
                {savingSettings ? "Enregistrement..." : "Enregistrer la configuration"}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* City / GPS repair tool */}
      <section style={{ marginBottom: "24px" }}>
        <div style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "14px", padding: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <MapPin size={16} style={{ color: "#f59e0b" }} />
            <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700 }}>Verifier & Reparer les Localisations</h2>
          </div>
          <p style={{ fontSize: "11px", color: "#6b7280", marginBottom: "12px", lineHeight: 1.4 }}>
            Les utilisateurs sans coordonnees GPS ne recoivent pas les notifs match. Liste les comptes a problemes et reattribue une ville pour geocoder leur position.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "12px" }}>
            <button
              data-testid="load-broken-cities-btn"
              onClick={loadBrokenCities}
              style={{
                minHeight: "44px", borderRadius: "10px",
                background: "#1a1a1a", border: "1px solid #2a2a2a",
                color: "#fff", fontSize: "13px", fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Scanner les comptes
            </button>
            <button
              data-testid="repair-all-cities-btn"
              onClick={handleRepairAll}
              disabled={isRepairing}
              style={{
                minHeight: "44px", borderRadius: "10px",
                background: isRepairing ? "#1a1a1a" : "linear-gradient(135deg,#f59e0b,#d97706)",
                border: "none", color: "#fff", fontSize: "13px", fontWeight: 700,
                cursor: isRepairing ? "not-allowed" : "pointer",
              }}
            >
              {isRepairing ? "Reparation..." : "Auto-reparer tout"}
            </button>
          </div>

          {brokenCities.length === 0 ? (
            <p style={{ fontSize: 12, color: "#6b7280", textAlign: "center", padding: "8px 0" }}>
              Clique sur "Scanner" pour lister les comptes incompletement geocodes.
            </p>
          ) : (
            <ul data-testid="broken-cities-list" style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              {brokenCities.map((u) => (
                <li key={u.id} data-testid={`broken-row-${u.id}`} style={{
                  background: "#000", border: "1px solid #1a1a1a", borderRadius: 10, padding: 10,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{u.name}</span>
                    <span style={{ fontSize: 10, color: "#f59e0b" }}>{u.city || "Aucune ville"}</span>
                  </div>
                  <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 8 }}>{u.email}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      data-testid={`repair-input-${u.id}`}
                      placeholder="Paris, Valenciennes, Lyon..."
                      value={cityRepairInputs[u.id] || ""}
                      onChange={(e) => setCityRepairInputs((prev) => ({ ...prev, [u.id]: e.target.value }))}
                      style={{
                        flex: 1, height: 36, padding: "0 10px", borderRadius: 8,
                        background: "#0a0a0a", border: "1px solid #2a2a2a", color: "#fff", fontSize: 12,
                      }}
                    />
                    <button
                      data-testid={`repair-btn-${u.id}`}
                      onClick={() => handleRepairUser(u.id)}
                      style={{
                        height: 36, padding: "0 12px", borderRadius: 8,
                        background: "#22c55e", border: "none", color: "#fff",
                        fontSize: 12, fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      Reparer
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Visiteurs anonymes (analytics) */}
      <section style={{ marginBottom: "24px" }}>
        <div style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "14px", padding: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <Eye size={16} style={{ color: "#22c55e" }} />
            <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700 }}>Visiteurs anonymes</h2>
          </div>
          <p style={{ fontSize: "11px", color: "#6b7280", marginBottom: "12px", lineHeight: 1.4 }}>
            Visiteurs uniques (page connexion) — sans compte cree. Compteur stable via UUID navigateur.
          </p>
          <div data-testid="analytics-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
            <div style={{ background: "#000", border: "1px solid #1a1a1a", borderRadius: "10px", padding: "10px", textAlign: "center" }}>
              <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px" }}>TOTAL</div>
              <div data-testid="anon-total" style={{ fontSize: "20px", fontWeight: 800, color: "#fff" }}>
                {analytics?.anonymous_visitors_total ?? "—"}
              </div>
            </div>
            <div style={{ background: "#000", border: "1px solid #1a1a1a", borderRadius: "10px", padding: "10px", textAlign: "center" }}>
              <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px" }}>30 JOURS</div>
              <div data-testid="anon-30d" style={{ fontSize: "20px", fontWeight: 800, color: "#22c55e" }}>
                {analytics?.anonymous_visitors_30d ?? "—"}
              </div>
            </div>
            <div style={{ background: "#000", border: "1px solid #1a1a1a", borderRadius: "10px", padding: "10px", textAlign: "center" }}>
              <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px" }}>7 JOURS</div>
              <div data-testid="anon-7d" style={{ fontSize: "20px", fontWeight: 800, color: "#22c55e" }}>
                {analytics?.anonymous_visitors_7d ?? "—"}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly recap panel */}
      <section style={{ marginBottom: "24px" }}>
        <div style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "14px", padding: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <Mail size={16} style={{ color: "#22c55e" }} />
            <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700 }}>Recap dimanche soir</h2>
          </div>
          <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "14px", lineHeight: 1.5 }}>
            Envoi automatique chaque dimanche a 19h00 (Europe/Paris) aux utilisateurs actifs (connectes dans les 30 derniers jours, opt-in).
            Tu peux declencher manuellement l'envoi ou tester en dry run / sur un email cible.
          </p>

          <input
            type="email"
            placeholder="email cible (optionnel pour test)"
            value={recapTargetEmail}
            onChange={(e) => setRecapTargetEmail(e.target.value)}
            data-testid="recap-target-email-input"
            style={{
              width: "100%", minHeight: "44px", padding: "10px 12px", borderRadius: "10px",
              background: "#000", border: "1px solid #1a1a1a", color: "#fff",
              fontSize: "13px", marginBottom: "10px",
            }}
          />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
            <button
              data-testid="recap-dry-run-btn"
              onClick={() => handleSendRecap({ dryRun: true, targeted: false })}
              disabled={isSendingRecap}
              style={{
                minHeight: "44px", borderRadius: "10px",
                background: "#1a1a1a", border: "1px solid #2a2a2a",
                color: "#fff", fontSize: "13px", fontWeight: 600,
                cursor: isSendingRecap ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              }}
            >
              {isSendingRecap ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Dry run
            </button>
            <button
              data-testid="recap-send-target-btn"
              onClick={() => handleSendRecap({ dryRun: false, targeted: true })}
              disabled={isSendingRecap}
              style={{
                minHeight: "44px", borderRadius: "10px",
                background: "#1a1a1a", border: "1px solid #2a2a2a",
                color: "#fff", fontSize: "13px", fontWeight: 600,
                cursor: isSendingRecap ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              }}
            >
              {isSendingRecap ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Envoyer (cible)
            </button>
          </div>

          <button
            data-testid="recap-send-all-btn"
            onClick={() => handleSendRecap({ dryRun: false, targeted: false })}
            disabled={isSendingRecap}
            style={{
              minHeight: "48px", width: "100%", borderRadius: "12px",
              background: isSendingRecap ? "#1a1a1a" : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              border: "none", color: "#fff", fontSize: "14px", fontWeight: 700,
              cursor: isSendingRecap ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            {isSendingRecap ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {isSendingRecap ? "Envoi en cours..." : "Envoyer a tous les actifs"}
          </button>
        </div>
      </section>

      {/* Users table */}
      <section>
        <h2 className="font-heading" style={{ fontSize: "15px", fontWeight: 700, marginBottom: "10px" }}>
          Utilisateurs ({users.length})
        </h2>

        <div data-testid="admin-users-table" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {users.length === 0 ? (
            <div style={{ padding: "24px", textAlign: "center", color: "#6b7280", fontSize: "13px", backgroundColor: "#0a0a0a", borderRadius: "12px", border: "1px solid #1a1a1a" }}>
              Aucun utilisateur en base.
            </div>
          ) : (
            users.map(u => (
              <div key={u.id} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "10px", padding: "12px 14px", backgroundColor: u.role === "banned" ? "rgba(239,68,68,0.06)" : "#0a0a0a", border: u.role === "banned" ? "1px solid rgba(239,68,68,0.4)" : "1px solid #1a1a1a", borderRadius: "12px" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: "#fff" }}>{u.name || "(sans nom)"}</span>
                    {u.role === "admin" && (
                      <span style={{ fontSize: "9px", padding: "2px 7px", borderRadius: "999px", backgroundColor: "rgba(234,179,8,0.15)", color: "#eab308", fontWeight: 700, letterSpacing: "0.05em" }}>ADMIN</span>
                    )}
                    {u.role === "banned" && (
                      <span style={{ fontSize: "9px", padding: "2px 7px", borderRadius: "999px", backgroundColor: "rgba(239,68,68,0.15)", color: "#f87171", fontWeight: 700, letterSpacing: "0.05em" }}>BANNI</span>
                    )}
                  </div>
                  <div style={{ fontSize: "12px", color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
                  <div style={{ fontSize: "11px", marginTop: "3px", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                    <MapPin size={10} style={{ color: u.city_lat != null ? "#22c55e" : "#f59e0b" }} />
                    <span style={{ color: "#fff", fontWeight: 600 }}>{u.city || "(aucune ville)"}</span>
                    {u.city_lat != null ? (
                      <span style={{ fontSize: 9, color: "#22c55e" }}>GPS ok</span>
                    ) : (
                      <span style={{ fontSize: 9, color: "#f59e0b" }}>GPS manquant</span>
                    )}
                  </div>
                  <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "4px" }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                    {" · "}
                    <span style={{ color: u.reliability >= 80 ? "#22c55e" : u.reliability >= 50 ? "#eab308" : "#ef4444" }}>
                      Fiab. {u.reliability}%
                    </span>
                    {" · "}
                    {u.matches_played} matchs
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignSelf: "center" }}>
                  <button
                    data-testid={`edit-user-${u.id}`}
                    onClick={() => setEditingUser(u)}
                    style={{ padding: "6px 8px", borderRadius: "8px", backgroundColor: "transparent", border: "1px solid #333", color: "#22c55e", cursor: "pointer", fontSize: "11px", display: "flex", alignItems: "center", gap: "4px" }}
                    aria-label="Editer"
                  >
                    <Edit3 size={12} /> Editer
                  </button>
                  {u.role !== "admin" && (
                    <button
                      data-testid={`delete-user-${u.id}`}
                      onClick={() => handleDelete(u.id, u.name)}
                      style={{ padding: "6px 8px", borderRadius: "8px", backgroundColor: "transparent", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171", cursor: "pointer", fontSize: "11px", display: "flex", alignItems: "center", gap: "4px" }}
                      aria-label="Supprimer"
                    >
                      <Trash2 size={12} /> Suppr.
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {editingUser && (
        <div data-testid="edit-user-modal" onClick={() => setEditingUser(null)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.85)", zIndex: 10001, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: "#0a0a0a", borderTopLeftRadius: "20px", borderTopRightRadius: "20px", padding: "20px", width: "100%", maxWidth: "440px", border: "1px solid #1a1a1a" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 800 }}>Editer {editingUser.name}</h3>
              <button onClick={() => setEditingUser(null)} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <EditForm user={editingUser} onSave={handleSaveUserEdit} onBan={() => handleSaveUserEdit({ role: editingUser.role === "banned" ? "user" : "banned" })} />
          </div>
        </div>
      )}
    </div>
  );
}

function EditForm({ user, onSave, onBan }) {
  const [reliability, setReliability] = useState(user.reliability ?? 100);
  const [rating, setRating] = useState(user.rating ?? 5.0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div>
        <label style={{ fontSize: "11px", color: "#9ca3af", textTransform: "uppercase", fontWeight: 600 }}>Fiabilite (%)</label>
        <input
          type="number" min="0" max="100"
          data-testid="edit-reliability"
          value={reliability}
          onChange={(e) => setReliability(parseInt(e.target.value) || 0)}
          style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", backgroundColor: "#000", border: "1px solid #333", color: "#fff", fontSize: "14px", marginTop: "4px" }}
        />
      </div>
      <div>
        <label style={{ fontSize: "11px", color: "#9ca3af", textTransform: "uppercase", fontWeight: 600 }}>Score (/10)</label>
        <input
          type="number" min="0" max="10" step="0.1"
          data-testid="edit-rating"
          value={rating}
          onChange={(e) => setRating(parseFloat(e.target.value) || 0)}
          style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", backgroundColor: "#000", border: "1px solid #333", color: "#fff", fontSize: "14px", marginTop: "4px" }}
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "4px" }}>
        <button data-testid="save-user-edit" onClick={() => onSave({ reliability, rating })}
          style={{ minHeight: "46px", borderRadius: "10px", background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", border: "none", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
          Enregistrer
        </button>
        <button data-testid="ban-user" onClick={onBan}
          style={{ minHeight: "46px", borderRadius: "10px", backgroundColor: user.role === "banned" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)", border: user.role === "banned" ? "1px solid rgba(34,197,94,0.3)" : "1px solid rgba(239,68,68,0.3)", color: user.role === "banned" ? "#22c55e" : "#f87171", fontSize: "13px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
          <Ban size={14} /> {user.role === "banned" ? "Reactiver" : "Bannir"}
        </button>
      </div>
    </div>
  );
}

function KPI({ label, value, icon, accent }) {
  return (
    <div style={{
      padding: "14px 16px",
      backgroundColor: accent ? "rgba(34,197,94,0.08)" : "#0a0a0a",
      border: accent ? "1px solid rgba(34,197,94,0.3)" : "1px solid #1a1a1a",
      borderRadius: "12px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "6px", color: accent ? "#22c55e" : "#9ca3af", fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: "24px", fontWeight: 800, color: "#fff" }}>{value}</div>
    </div>
  );
}
